import { useState, useEffect } from 'react';
import { X, Check, Lock, Loader2, LogOut } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useAuth } from '@hooks/useAuth';
import { fetchCurrentSubscription } from '@store/slices/subscriptionSlice';
import PlanService from '@services/api/plan.service';
import { mapPlansToFrontend } from '@utils/planMapper';
import toast from 'react-hot-toast';
import RazorpayCheckout from '@components/payment/RazorpayCheckout';

const PlanUpgradeModal = ({ isOpen, onClose, mandatory = false }) => {
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activatingPlanId, setActivatingPlanId] = useState(null);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [hasUsedFreePlan, setHasUsedFreePlan] = useState(false);

  useEffect(() => {
    if (isOpen && plans.length === 0) {
      fetchPlans();
    }
  }, [isOpen]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all plans
      const response = await PlanService.getAllPlans();
      
      if (response.success && response.data) {
        const mappedPlans = mapPlansToFrontend(response.data);
        setPlans(mappedPlans);
      }
      
      // Fetch current plan and hasUsedFreePlan status
      try {
        const currentPlanResponse = await PlanService.getCurrentPlan();
        if (currentPlanResponse.success && currentPlanResponse.data?.plan) {
          setCurrentPlanId(currentPlanResponse.data.plan.id);
          setHasUsedFreePlan(currentPlanResponse.data.hasUsedFreePlan || false);
        }
      } catch (err) {
        console.log('No active plan found');
        setHasUsedFreePlan(false);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanClick = async (planId, planSlug, planPrice, planName) => {
    console.log('🎯 [Modal] Plan clicked:', { planId, planSlug, planPrice, planName });
    
    // If free plan already used, show error
    if (planSlug === 'free' && hasUsedFreePlan) {
      toast.error('You have already used the free plan. Please choose a paid plan to continue.', {
        duration: 5000,
        icon: '⚠️'
      });
      return;
    }

    setActivatingPlanId(planId);

    // Free plan - activate directly
    if (planPrice === 0) {
      console.log('💚 [Modal] Free plan detected, using direct activation');
      await handleDirectActivation(planId);
      return;
    }

    // Paid plan - Razorpay payment flow
    console.log('💰 [Modal] Paid plan detected, starting Razorpay flow');
    await handleRazorpayPayment(planId, planPrice, planName);
  };

  // PRODUCTION: Razorpay Payment Flow
  const handleRazorpayPayment = async (planId, planPrice, planName) => {
    try {
      console.log('🔵 [Modal] Starting Razorpay payment flow...');
      
      const orderResponse = await PlanService.createPaymentOrder(planId);
      console.log('🔵 [Modal] Order response:', orderResponse);
      
      if (!orderResponse.success) {
        throw new Error('Failed to create payment order');
      }

      const { orderId, amount, currency } = orderResponse.data;
      console.log('🔵 [Modal] Order created:', { orderId, amount, currency });

      await RazorpayCheckout.initiatePayment({
        orderId,
        amount,
        currency,
        planName,
        onOpen: () => {
          onClose();
        },
        onClose: () => {
          if (isOpen) return;
        },
        onSuccess: async (paymentData) => {
          // console.log('✅ [Modal] Payment successful:', paymentData);
          await handlePaymentSuccess(paymentData);
        },
        onFailure: (error) => {
          console.error('❌ [Modal] Payment failed:', error);
          handlePaymentFailure(error);
        }
      });
    } catch (error) {
      console.error('❌ [Modal] Payment initiation error:', error);
      toast.error(error.message || 'Failed to initiate payment. Please try again.');
      setActivatingPlanId(null);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      const response = await PlanService.verifyPayment(paymentData);
      
      if (response.success) {
        toast.success(`Payment successful! Plan ${response.data.action} successfully! 🎉`, {
          duration: 5000
        });
        
        await dispatch(fetchCurrentSubscription());
        
        await fetchPlans();
        
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Payment verification failed. Please contact support if amount was deducted.');
    } finally {
      setActivatingPlanId(null);
    }
  };

  const handlePaymentFailure = (error) => {
    console.error('Payment failed:', error);
    toast.error(error || 'Payment failed. Please try again.');
    setActivatingPlanId(null);
  };

  // Handle logout for mandatory mode
  const handleLogout = async () => {
    try {
      await logout();
      // Use window.location instead of navigate to avoid Router context issues
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout. Please try again.');
    }
  };

  // For free plans only
  const handleDirectActivation = async (planId) => {
    try {
      const response = await PlanService.activatePlan(planId);
      
      if (response.success) {
        toast.success(`Plan ${response.data.action} successfully! 🎉`);
        await dispatch(fetchCurrentSubscription());
        
        await fetchPlans();
        
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Plan activation error:', error);
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || 'Failed to activate plan. Please try again.';
      const errorCode = errorData?.error;
      
      if (errorCode === 'FREE_PLAN_ALREADY_USED' || errorMessage.includes('already used the free plan')) {
        toast.error('You have already used the free plan. Please choose a paid plan to continue.', {
          duration: 5000,
          icon: '⚠️'
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setActivatingPlanId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-1 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-7xl max-h-[98dvh] sm:max-h-[90vh] bg-white rounded-xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-6 flex items-center justify-between rounded-t-xl sm:rounded-t-2xl z-10">
          <div className={`flex-1 text-center ${!mandatory ? 'pr-8' : ''}`}>
            <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 leading-tight">
              {mandatory ? (
                <>Choose Your <span className="text-[#1a2642]">Plan to Continue</span></>
              ) : (
                <>Find the <span className="text-[#1a2642]">Right Plan.</span></>
              )}
            </h2>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-1 max-w-2xl mx-auto">
              {mandatory 
                ? 'Select a plan to start using our platform. Choose Free to get started instantly!' 
                : 'Create your own website with the fastest Page Building Platform.'}
            </p>
          </div>
          {!mandatory && (
            <button
              onClick={onClose}
              className="absolute top-2.5 right-2.5 sm:top-4 sm:right-4 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              aria-label="Close modal"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Plans Grid — scrolls internally, header/footer stay fixed so modal never exceeds the viewport */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4">
          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-gray-600">Loading plans...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button 
                onClick={fetchPlans}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* Plans — horizontal swipeable slider on mobile, grid from sm breakpoint up */}
          {!loading && !error && plans.length > 0 && (
            <>
              <style>{`.plan-slider::-webkit-scrollbar{display:none}`}</style>
              {plans.length > 1 && (
                <p className="text-[10px] text-gray-400 text-center mb-2 sm:hidden">← Swipe to see more plans →</p>
              )}
              <div className={`plan-slider flex sm:grid gap-3 sm:gap-3.5 items-stretch overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:snap-none scroll-smooth -mx-3 px-3 sm:mx-0 sm:px-0 ${
                plans.length === 1 ? 'sm:grid-cols-1 sm:max-w-md sm:mx-auto' :
                plans.length === 2 ? 'sm:grid-cols-2 sm:max-w-3xl sm:mx-auto' :
                plans.length === 3 ? 'sm:grid-cols-2 lg:grid-cols-3 sm:max-w-5xl sm:mx-auto' :
                'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              }`} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`group relative rounded-lg sm:rounded-xl p-2.5 sm:p-4 md:p-5 transition-all duration-300 ease-out flex flex-col bg-white hover:bg-[#1a2642] hover:shadow-2xl hover:border-[#1a2642] min-w-[82%] max-w-[82%] flex-shrink-0 snap-center sm:min-w-0 sm:max-w-none sm:flex-shrink sm:snap-align-none ${
                    plan.popular 
                      ? 'border-2 border-[#1a2642] shadow-lg' 
                      : 'border border-gray-200'
                  }`}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute -top-2 sm:-top-3 left-1/2 -translate-x-1/2 z-10">
                      <span className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-[#c33c3d] text-white text-[9px] sm:text-xs font-semibold shadow-md whitespace-nowrap">
                        Popular
                      </span>
                    </div>
                  )}

                  {/* Plan Name */}
                  <h3 className="text-sm sm:text-lg md:text-xl font-bold mb-1.5 sm:mb-3 text-gray-900 group-hover:text-white transition-colors leading-tight">
                    {plan.name}
                  </h3>

                  {/* Price */}
                  <div className="mb-2 sm:mb-4">
                    <div className="flex items-baseline gap-1 flex-wrap">
                      <span className="text-lg sm:text-2xl md:text-3xl font-bold text-[#c33c3d] group-hover:text-white transition-colors">
                        ₹{plan.price}
                      </span>
                      <span className="text-[10px] sm:text-sm text-gray-600 group-hover:text-white/80 transition-colors">
                        /{plan.billingCycle === 'weekly' ? 'week' : plan.billingCycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    </div>
                    <p className="text-[8px] sm:text-[10px] text-gray-500 group-hover:text-white/70 transition-colors mt-0.5">
                      then incl. GST
                    </p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-1 sm:space-y-2 mb-2 sm:mb-4 flex-grow overflow-y-auto max-h-[170px] sm:max-h-[220px] md:max-h-none">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-1 sm:gap-2">
                        {feature.accessible ? (
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5 text-[#1a2642] group-hover:text-white transition-colors" />
                        ) : (
                          <Lock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5 text-gray-400 group-hover:text-white/60 transition-colors" />
                        )}
                        <span className={`text-[9px] sm:text-xs font-semibold transition-colors leading-tight break-words ${
                          feature.accessible 
                            ? 'text-gray-700 group-hover:text-white' 
                            : 'text-gray-400 group-hover:text-white/60'
                        }`}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button or Active Badge */}
                  {currentPlanId === plan.id ? (
                    <div className="w-full py-1.5 sm:py-2.5 rounded-lg text-[10px] sm:text-sm font-semibold mt-auto bg-green-500 text-white border-2 border-green-500 shadow-md flex items-center justify-center gap-1 sm:gap-1.5">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>Active Plan</span>
                    </div>
                  ) : (
                    <button 
                      onClick={() => handlePlanClick(plan.id, plan.slug, plan.price, plan.name)}
                      disabled={activatingPlanId === plan.id || (plan.slug === 'free' && hasUsedFreePlan)}
                      className="w-full py-1.5 sm:py-2.5 rounded-lg text-[10px] sm:text-sm font-semibold transition-all mt-auto bg-navbar text-white border border-navbar hover:bg-navbar-hover group-hover:bg-white group-hover:text-navbar group-hover:border-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 sm:gap-1.5"
                    >
                      {activatingPlanId === plan.id ? (
                        <>
                          <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                          <span>{plan.price !== 0 ? 'Processing...' : 'Activating...'}</span>
                        </>
                      ) : plan.slug === 'free' && hasUsedFreePlan ? (
                        <span>Already Used</span>
                      ) : (
                        <span>{plan.price !== 0 ? 'Get Started' : 'Start Free Trial'}</span>
                      )}
                    </button>
                  )}
                </div>
              ))}
              </div>
            </>
          )}
        </div>

        {/* Footer with Logout - Only for Mandatory Mode */}
        {mandatory && (
          <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-b-xl sm:rounded-b-2xl">
            <p className="text-xs sm:text-sm text-gray-600">
              Need to check something first?
            </p>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-gray-400 transition-all shadow-sm hover:shadow"
            >
              <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanUpgradeModal;
